import {DatasetDefaults, LayerDef, LayerType} from "../types";
import {Dataset, DatasetRow} from "../data/Dataset";
import {Color, ColorMap} from "../util/Color";
import MapTooltip from "./MapTooltip";
import React from "react";
import {ColorUtils} from "../util/ColorUtils";
import {getAppState, getBootstrapParams} from "../store/AppState";
import {getMapComponent} from "../store/Accessor";


export class LayerUtil {

    public static createLayerDef = (id: string, name: string, dataset: Dataset, type: LayerType = "point"): LayerDef => {
        const defaults: DatasetDefaults = getBootstrapParams().getDatasetDefaults(dataset.getType());

        let palette: string[];
        if (defaults?.colors && defaults?.colors.length > 0) {
            palette = defaults?.colors;
        } else {
            palette = ColorUtils.createNewPaletteForDataset(dataset);
        }

        let safeId = getAppState().getUniqueLayerId(id);
        return new LayerDef({
            id: safeId,
            name: name,
            type: type,
            geoFieldType: "geometry",
            dataset: dataset,
            headingFieldId: defaults?.directionField ?? dataset.getDatasetInfo().getHeadingField(),
            isHidden: false,
            config: {
                colorPalette: palette.map((c: string) => Color.createFromString(c)),
                colorsMapsByFields: {},
                useHeadingField: true,
                opacity: defaults?.opacity ?? 100,
                size: defaults?.pointSize ?? 20,
                sizeFieldId: defaults?.sizeField ?? dataset.getFirstFloatFieldDef()?.id,
                sizeDependsOnSizeField: !!defaults?.sizeField,
                tooltipFields: defaults?.tooltipFields ?? [],
                shape: defaults?.shape ?? "circle"
            }
        });
    }

    public static createTooltip = (info, layerDef: LayerDef, pinned = false) => {
        let data: DatasetRow[];
        if (info["objects"]) {
            data = info["objects"] as DatasetRow[];
        } else if (info.object) {
            data = [info.object as DatasetRow];
        }
        let tt = null;
        if (data) {
            tt =  <MapTooltip
                key={`tt_${info.x}_${info.y}`}
                x={info.x}
                y={info.y}
                layerDef={layerDef}
                data={data}
                pinned={pinned}
                onEscape={() => getMapComponent().setTooltip(null)}
            />;
        }
        return tt;
    }

    /**
     * Filter data rows by not hidden colors
     * @param layerDef
     * @param buckets - Used only for numeric color fields
     */
    public static prepareDataRows = (layerDef: LayerDef, dataRows: DatasetRow[], buckets: number[]): DatasetRow[] => {
        if (layerDef.config.hiddenColors) {
            if (layerDef.isColorFieldNumeric()) {
                dataRows = dataRows.filter(row => {
                        const colIndex = ColorUtils.getColorIndexForValue(row.getValue(layerDef.config.colorFieldId), buckets);
                        return !layerDef.config.hiddenColors.has(colIndex);
                })
            } else {
                const colorMap = LayerUtil.prepareColorMap(layerDef);
                const isDefaultColorHidden = layerDef.config.hiddenColors.has("");
                dataRows = dataRows.filter(row => {
                    let v = row.getValue(layerDef.config.colorFieldId);
                    return colorMap[v] ? !layerDef.config.hiddenColors.has(v) : !isDefaultColorHidden;
                })
            }
        }

        return dataRows;
    }


    public static calcFillColor = (layerDef: LayerDef, dataRow: DatasetRow, buckets: number[]): Color => {
        const layerConfig = layerDef.config;
        if (!layerDef.isColorFieldNumeric()) {
            const colorMap = LayerUtil.prepareColorMap(layerDef);
            const matchingColor = colorMap[dataRow.getValue(layerConfig.colorFieldId)] ?? colorMap[""] ?? "#FF0000";
            return Color.createFromString(matchingColor);
        } else {
            return ColorUtils.getColorFromPaletteForValue(
                dataRow.getNumberValue(layerConfig.colorFieldId),
                buckets,
                layerDef.config.colorPalette,
                layerDef.config.useGradient,
            );
        }
    }


    public static prepareColorMap = (layerDef: LayerDef): ColorMap => {
        let colorsMap = layerDef.config.colorsMapsByFields[layerDef.config.colorFieldId];
        if (!colorsMap) {
            colorsMap = {}
        }
        if (!colorsMap[""]) {
            // ensure there is a default color
            colorsMap[""] = "#0077DD";
        }
        return colorsMap;
    }

}